import { useState } from "react";
import { toast } from "react-toastify";
import { accountService } from "../../services/api";
import { ModalShell, btnGhost, btnPrimary } from "../Pages/SOS/sosCommon";

// Same rules the server enforces (it is the source of truth; this just gives instant feedback).
const RULES = [
  { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "letter", label: "A letter", test: (p) => /[A-Za-z]/.test(p) },
  { key: "digit", label: "A number", test: (p) => /\d/.test(p) },
];

function PasswordField({ id, label, value, onChange, autoComplete, autoFocus, error }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={`w-full pr-16 ${error ? "border-red-400" : ""}`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

export default function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const passed = RULES.filter((r) => r.test(next)).length;
  const mismatch = confirm.length > 0 && confirm !== next;
  const sameAsOld = next.length > 0 && next === current;
  const ready = current && passed === RULES.length && next === confirm && !sameAsOld && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      await accountService.changePassword({
        adminId: localStorage.getItem("adminId") || "",
        currentPassword: current,
        newPassword: next,
      });
      toast.success("Password changed. Use the new password next time you sign in.");
      onClose();
    } catch (err) {
      setError(err.message || "Could not change the password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Change password"
      onClose={busy ? () => {} : onClose}
      wide="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form="change-password-form" className={btnPrimary} disabled={!ready}>
            {busy ? "Saving…" : "Change password"}
          </button>
        </div>
      }
    >
      <form id="change-password-form" onSubmit={submit} className="space-y-4">
        <PasswordField id="cp-current" label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" autoFocus />
        <div>
          <PasswordField id="cp-new" label="New password" value={next} onChange={setNext} autoComplete="new-password" error={sameAsOld ? "New password must be different from the current one" : ""} />
          <div className="mt-2 flex h-1.5 gap-1" aria-hidden="true">
            {RULES.map((r, i) => (
              <span key={r.key} className={`flex-1 rounded-full ${i < passed ? (passed === RULES.length ? "bg-emerald-500" : "bg-amber-400") : "bg-slate-200"}`} />
            ))}
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {RULES.map((r) => (
              <li key={r.key} className={r.test(next) ? "text-emerald-600" : "text-slate-500"}>
                {r.test(next) ? "✓" : "○"} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <PasswordField id="cp-confirm" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" error={mismatch ? "Passwords do not match" : ""} />
        {error ? <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
      </form>
    </ModalShell>
  );
}
